import Popover from "@mui/material/Popover"
import { ChevronDown, EyeOff } from "lucide-mui"
import React, { useRef, useState } from "react"

import { useVenue } from "@/contexts/venue"
import i18n from "@/i18n"
import { useStore } from "@/store"

const TableGroups = () => {
  const { venue } = useVenue()
  const { selectedTableGroupIds: selected, setSelectedTableGroupIds: setSelected } = useStore()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  if (!venue || !venue.tableGroups) return null

  const activeTableGroups = venue.tableGroups.filter((tg) => !tg.deleted)
  const allSelected = selected.length === activeTableGroups.length

  const handleToggle = (id: string, event: React.MouseEvent) => {
    const isModified = event.metaKey || event.ctrlKey
    if (isModified) {
      if (selected.length === 1 && selected[0] === id) {
        setSelected(activeTableGroups.map((tg) => tg.id))
      } else {
        setSelected([id])
      }
      return
    }

    if (selected.includes(id)) {
      if (selected.length <= 1) return
      setSelected(selected.filter((s) => s !== id))
    } else {
      setSelected([...selected, id])
    }
  }

  return (
    <div data-testid="panel-reservations-table-groups">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={i18n.t("app.reservations_navigation.rooms")}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center gap-1 px-[15px] h-8 rounded-[5px] text-xs font-medium cursor-pointer ${
          allSelected
            ? "bg-white border border-[#eee] text-neutral-700"
            : "bg-blue-100 border border-blue-300 text-blue-800"
        }`}
        data-testid="panel-reservations-table-groups-select"
      >
        {i18n.t("app.reservations_navigation.rooms")}
        <ChevronDown className="w-3 h-3" />
      </button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { className: "mt-1 rounded-md shadow-lg min-w-[180px]" } }}
      >
        <ul
          role="listbox"
          aria-multiselectable="true"
          className="py-1"
          data-testid="panel-reservations-table-groups-menu"
        >
          {activeTableGroups.map((tableGroup) => {
            const isSelected = selected.includes(tableGroup.id)
            return (
              <li
                key={tableGroup.id}
                role="option"
                aria-selected={isSelected}
                onClick={(e) => handleToggle(tableGroup.id, e)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleToggle(tableGroup.id, e as any)
                  }
                }}
                className={`flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-100 ${
                  isSelected ? "text-gray-900" : "text-gray-400"
                }`}
                data-testid={`panel-reservations-table-groups-option-${tableGroup.id}`}
              >
                <span>{tableGroup.name}</span>
                {!isSelected && <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
              </li>
            )
          })}
        </ul>
      </Popover>
    </div>
  )
}

export default TableGroups
